import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { AttioConsultantData, AttioConsultantService } from 'dashboard409a/services/attio/AttioConsultantService';
import { AttioTeamData } from 'dashboard409a/services/attio/AttioTeamService';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';

interface UseOtherConsultantsProps {
  valuationRow: SimplifiedAttioValuation;
  onOtherConsultantsUpdate?: (
    recordId: string,
    otherConsultantOptions: { targetRecordId: string; name: string; imageSrc: string }[]
  ) => Promise<void>;
}

/** Bulk / list payloads may return arrays or a single string; local updates may store string[] cast as string. */
function normalizeWireFieldToStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(v => (v == null ? '' : String(v))).filter(Boolean);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t === '-') {
      return [];
    }
    return [t];
  }
  return [];
}

const buildInitialConsultants = (row: SimplifiedAttioValuation): AttioTeamData[] => {
  const names = normalizeWireFieldToStringArray(row.other_consultant as unknown);
  const ids = normalizeWireFieldToStringArray(row.other_consultant_id as unknown);
  const images = normalizeWireFieldToStringArray(row.other_consultant_image_src as unknown);

  // Prefer IDs as source of truth when the API returns multiple references but sparse name/image arrays.
  if (ids.length > 0) {
    return ids.map((targetRecordId, index) => ({
      targetRecordId,
      name: names[index] ?? '-',
      imageSrc: images[index] ?? '',
    }));
  }

  return names.map((name, index) => ({
    targetRecordId: ids[index] ?? '',
    name,
    imageSrc: images[index] ?? '',
  }));
};

function needsConsultantHydration(c: AttioTeamData): boolean {
  return (
    Boolean(c.targetRecordId && c.targetRecordId !== '-') &&
    (!c.name || c.name === '-' || c.name.trim() === '')
  );
}

export const useOtherConsultants = ({ valuationRow, onOtherConsultantsUpdate }: UseOtherConsultantsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [consultants, setConsultants] = useState<AttioConsultantData[]>([]);
  const [selectedConsultants, setSelectedConsultants] = useState<AttioTeamData[]>(() =>
    buildInitialConsultants(valuationRow)
  );
  const [isDirty, setIsDirty] = useState(false);
  const consultantService = useMemo(() => new AttioConsultantService(), []);
  const { enqueueSnackbar } = useSnackbar();

  const open = Boolean(anchorEl);
  const popoverId = open ? 'other-consultant-popover' : undefined;

  // Re-sync selection from valuationRow each time the popover opens; fetch names for IDs when list data is sparse.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const initial = buildInitialConsultants(valuationRow);
    setSelectedConsultants(initial);
    setIsDirty(false);

    if (!initial.some(needsConsultantHydration)) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const next = await Promise.all(
        initial.map(async c => {
          if (!needsConsultantHydration(c)) {
            return c;
          }
          const data = await consultantService.getOtherConsultant(c.targetRecordId);
          if (cancelled) {
            return c;
          }
          return {
            targetRecordId: c.targetRecordId,
            name: data.name,
            imageSrc: data.imageSrc && data.imageSrc !== '-' ? data.imageSrc : c.imageSrc,
          };
        })
      );
      if (!cancelled) {
        setSelectedConsultants(next);
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateMutation = useMutation({
    mutationFn: ({
      valuationId,
      otherConsultantTargetRecordIds,
    }: {
      valuationId: string;
      otherConsultantTargetRecordIds: string[];
      snapshot: AttioTeamData[];
    }) => consultantService.updateValuationOtherConsultants({ valuationId, otherConsultantTargetRecordIds }),
    onSuccess: async (_, variables) => {
      if (onOtherConsultantsUpdate) {
        const options = variables.snapshot.map(c => ({
          targetRecordId: c.targetRecordId,
          name: c.name,
          imageSrc: c.imageSrc,
        }));
        await onOtherConsultantsUpdate(variables.valuationId, options);
      }
      enqueueSnackbar('Other consultants updated successfully!', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update other consultants. Please try again.', { variant: 'error' });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleToggleConsultant = (consultant: AttioTeamData) => {
    setIsDirty(true);
    setSelectedConsultants(prev =>
      prev.some(m => m.targetRecordId === consultant.targetRecordId)
        ? prev.filter(m => m.targetRecordId !== consultant.targetRecordId)
        : [...prev, consultant]
    );
  };

  const handleClose = () => {
    const snapshot = [...selectedConsultants];
    const dirty = isDirty;

    setAnchorEl(null);
    setSearchTerm('');
    setConsultants([]);
    setIsDirty(false);

    if (dirty) {
      updateMutation.mutate({
        valuationId: valuationRow.record_id,
        otherConsultantTargetRecordIds: snapshot.map(c => c.targetRecordId),
        snapshot,
      });
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setConsultants([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await consultantService.searchConsultants(searchTerm);
        setConsultants(result as any);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, consultantService]);

  return {
    anchorEl,
    searchTerm,
    searching,
    consultants,
    selectedConsultants,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleToggleConsultant,
    setSearchTerm,
    isUpdating: updateMutation.isPending,
  };
};
